import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Card,
  InputGroup,
} from "react-bootstrap";
import { Plus, X, Settings, Code as CodeIcon, Cpu, Copy } from "lucide-react";
import { VizPropsBuilder } from "./VizPropsBuilder";
import { ClipLoader } from "react-spinners";
import { Switch } from "components/ui/switch";
import CustomSelect from "../../Components/CustomSelect";
import { getTemplateTypes, LLM_MODELS } from "services/constants";
import localization from "services/localization";
import ResourceManagerModal from "views/Components/ResourceLibrary/ResourceManagerModal";
import { useUserStore } from "context/useUserStore";
import SpecificAPI from "services/SpecificApi";
import ResourceComponent from "views/Components/ResourceLibrary/ResourceComponent";
import PaidIcon from "@mui/icons-material/PaidOutlined";
import { toast } from "react-toastify";

export default function AddEditPromptTemplate({
  show,
  onClose,
  onSubmit,
  formData,
  title,
  loading,
  onChange,
  onChangeNested,
  errors,
}) {
  const [newVariable, setNewVariable] = useState("");
  const [theme, setTheme] = useState(
    localStorage.getItem("SA_user_theme") || "light",
  );
  const TEMPLATE_TYPES = getTemplateTypes();

  useEffect(() => {
    setTheme(localStorage.getItem("SA_user_theme") || "light");
  }, [show]);

  const addVariable = () => {
    const v = newVariable.trim();
    const currentVars = formData.inputs?.instruction_variables || [];
    if (v && !currentVars.includes(v)) {
      onChangeNested("inputs.instruction_variables", [...currentVars, v]);
      setNewVariable("");
    }
  };

  const removeVariable = (variable) => {
    const currentVars = formData.inputs?.instruction_variables || [];
    onChangeNested(
      "inputs.instruction_variables",
      currentVars.filter((v) => v !== variable),
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <>
      <Modal
        scrollable
        show={show}
        onHide={onClose}
        size="xl"
        backdrop="static"
        centered
        className="themed-modal bootstrapModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={onSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group className="required">
                  <Form.Label className="bootstrap-label">
                    {localization.template_name || "Template Name"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      localization.enter_template_name || "Enter template name"
                    }
                    value={formData.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group className="required">
                  <Form.Label className="bootstrap-label">
                    {localization.type || "Type"}
                  </Form.Label>
                  <CustomSelect
                    options={TEMPLATE_TYPES}
                    value={TEMPLATE_TYPES.find(
                      (opt) => opt.value === formData.type,
                    )}
                    onChange={(opt) => onChange("type", opt?.value || "")}
                    placeholder={localization.select_a_type || "Select a type"}
                  />
                  {errors.type && (
                    <div className="text-danger small mt-1">{errors.type}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.description || "Description"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder={
                      localization.brief_description_of_the_template ||
                      "Brief description of the template"
                    }
                    value={formData.description}
                    onChange={(e) => onChange("description", e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* System Prompt */}
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.system_prompt_content ||
                      "System Prompt Content"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={formData.system_prompt}
                    onChange={(e) => onChange("system_prompt", e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.user_prompt_content || "User Prompt Content"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={formData.user_prompt}
                    onChange={(e) => onChange("user_prompt", e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.query_rag_prompt || "Query RAG prompt"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={formData.text_to_rag}
                    onChange={(e) => onChange("text_to_rag", e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Instruction Variables */}
              <Col md={12} className="mb-3">
                <Card className="mb-4 shadow-sm instruction-card">
                  {/* Card Header */}
                  <div className="instruction-card-header">
                    <CodeIcon size={20} className="text-primary" />
                    <h5 className="mb-0 font-semibold">
                      {localization.instruction_variables ||
                        "Instruction Variables"}
                    </h5>
                  </div>

                  {/* Card Body */}
                  <Card.Body>
                    <Row>
                      {/* Description */}
                      <Col md={12} className="mb-3">
                        <Form.Text className="description-text">
                          {localization.variables_injected_into_the_prompt ||
                            "Variables injected into the prompt (e.g., histories.title)"}
                        </Form.Text>
                      </Col>

                      {/* Input + Add button */}
                      <Col md={6} className="mb-3 d-flex gap-2">
                        <Form.Control
                          placeholder={
                            localization.variable_name_placeholder ||
                            "variable.name"
                          }
                          value={newVariable}
                          onChange={(e) => setNewVariable(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            (e.preventDefault(), addVariable())
                          }
                        />
                        <Button
                          variant="primary"
                          onClick={addVariable}
                          className="d-flex align-items-center justify-content-center"
                          style={{ width: "40px" }}
                        >
                          <Plus size={16} />
                        </Button>
                      </Col>

                      {/* Variable badges */}
                      <Col md={12}>
                        <div className="d-flex flex-wrap gap-2">
                          {(formData.inputs?.instruction_variables || []).map(
                            (v) => (
                              <Badge
                                key={v}
                                bg="secondary"
                                className="instruction-variable-badge d-flex align-items-center gap-1 px-2 py-1"
                              >
                                <code>{v}</code>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="instruction-variable-remove p-0"
                                  onClick={() => removeVariable(v)}
                                >
                                  <X size={12} />
                                </Button>
                              </Badge>
                            ),
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={12} className="mb-3">
                <Card className="mb-4 shadow-sm instruction-card">
                  <div className="instruction-card-header">
                    <Cpu size={20} className="text-primary" />
                    <h5 className="mb-0 font-semibold">
                      {localization.model_parameters || "Model Parameters"}
                    </h5>
                  </div>
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label className="bootstrap-label">
                          {localization.llm_model || "LLM Model"}
                        </Form.Label>
                        <CustomSelect
                          options={LLM_MODELS}
                          value={LLM_MODELS.find(
                            (opt) =>
                              opt.value ===
                              formData.inputs?.model_parameters?.model,
                          )}
                          onChange={(opt) =>
                            onChangeNested(
                              "inputs.model_parameters.model",
                              opt?.value || "",
                            )
                          }
                          placeholder={
                            localization.select_llm_model || "Select LLM model"
                          }
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Label className="bootstrap-label">
                          {localization.max_output_tokens ||
                            "Max Output Tokens"}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          value={
                            formData.inputs?.model_parameters?.max_output_tokens
                          }
                          onChange={(e) =>
                            onChangeNested(
                              "inputs.model_parameters.max_output_tokens",
                              Number(e.target.value),
                            )
                          }
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Label className="bootstrap-label">
                          {localization.temperature || "Temperature"}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step={0.1}
                          min={0}
                          max={1}
                          value={formData.inputs?.model_parameters?.temperature}
                          onChange={(e) =>
                            onChangeNested(
                              "inputs.model_parameters.temperature",
                              parseFloat(e.target.value),
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              {/* Settings */}
              <Col md={12} className="mb-4">
                <Card className="mb-4 shadow-sm instruction-card">
                  {/* Header */}
                  <div className="instruction-card-header">
                    <div className="d-flex align-items-center gap-2">
                      <Settings size={20} className="text-primary" />
                      <h5 className="mb-0 font-semibold">
                        {localization.settings || "Settings"}
                      </h5>
                    </div>
                  </div>

                  {/* Body */}
                  <Card.Body>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.rag_enabled || "RAG Enabled"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.enable_retrieval_augmented_generation ||
                                "Enable Retrieval-Augmented Generation"}
                            </Form.Text>
                          </div>

                          <div className="d-flex align-items-center">
                            <Switch
                              checked={formData.rag}
                              onCheckedChange={(value) =>
                                onChange("rag", value)
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.html_format || "HTML Format"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.output_responses_in_html_format ||
                                "Output responses in HTML format"}
                            </Form.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Switch
                              checked={formData.html_format}
                              onCheckedChange={(value) =>
                                onChange("html_format", value)
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.can_edit_response ||
                                "Can edit response"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.allow_user_to_edit_response ||
                                "Allow user to edit response"}
                            </Form.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Switch
                              checked={formData.response_is_editable}
                              onCheckedChange={(value) =>
                                onChange("response_is_editable", value)
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.chat_visible_to_members ||
                                "Make Chat visible To Members"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.chat_visible_to_members ||
                                "Make Chat visible To Members"}
                            </Form.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Switch
                              checked={
                                formData.system_props.chat_visible_to_members
                              }
                              onCheckedChange={(value) =>
                                onChangeNested(
                                  "system_props.chat_visible_to_members",
                                  value,
                                )
                              }
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.can_user_chat || "Can user chat"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.allow_user_to_chat ||
                                "Allow user to chat"}
                            </Form.Text>
                          </div>
                          <div className="d-flex align-items-center">
                            <Switch
                              checked={formData.system_props.can_user_chat}
                              onCheckedChange={(value) =>
                                onChangeNested(
                                  "system_props.can_user_chat",
                                  value,
                                )
                              }
                            />
                          </div>
                        </Form.Group>

                        <Form.Label className="bootstrap-label">
                          {localization.prompt_cost || "Prompt Cost"}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            step={1}
                            min={0}
                            value={formData.system_props?.prompt_cost}
                            onChange={(e) =>
                              onChangeNested(
                                "system_props.prompt_cost",
                                e.target.value,
                              )
                            }
                          />
                          <InputGroup.Text>
                            <PaidIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                          </InputGroup.Text>
                        </InputGroup>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Group className="d-flex align-items-start mb-3">
                          <div className="me-2 switch-text">
                            <Form.Label className="bootstrap-label">
                              {localization.can_user_select_information_source ||
                                "Make Users Select Information Source"}
                            </Form.Label>
                            <Form.Text className="description-text d-block">
                              {localization.can_user_select_information_source_desc ||
                                "Allow users to select which information Source to use (resource / AI Knowledge / both) "}
                            </Form.Text>
                            <span
                              className="mt-2 d-inline-flex align-items-center gap-1 px-2 py-1 border border-gray-400 rounded cursor-pointer text-sm ms-auto"
                              onClick={() =>
                                copyToClipboard(`{information_source}`)
                              }
                              title="Copy field reference"
                              style={{ cursor: "pointer" }}
                            >
                              {"{information_source}"}
                              <Copy size={12} />
                            </span>
                          </div>
                          <div className="d-flex align-items-center">
                            <Switch
                              checked={
                                formData.system_props
                                  .can_user_select_information_source
                              }
                              onCheckedChange={(value) => {
                                onChangeNested(
                                  "system_props.can_user_select_information_source",
                                  value,
                                );
                                if (value == true) {
                                  var inputObj = {
                                    inputType: "select",
                                    name: "information_source",
                                    label: "Information Source",
                                    description: "",
                                    placeholder: "Information Source",
                                    required: true,
                                    options: [
                                      "AI only",
                                      "Selected Resource only",
                                      "AI & Selected Resource",
                                    ],
                                    showResourceSourceOptions: false,
                                    resourceType: null,
                                  };
                                  var newData = [
                                    inputObj,
                                    ...formData.system_props?.viz_props,
                                  ];
                                  onChangeNested(
                                    "system_props.viz_props",
                                    newData,
                                  );
                                } else {
                                  var newData =
                                    formData.system_props?.viz_props?.filter(
                                      (props) =>
                                        props.name != "information_source",
                                    );
                                  onChangeNested(
                                    "system_props.viz_props",
                                    newData,
                                  );
                                }
                              }}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Add more switches here if needed */}
                  </Card.Body>
                </Card>
              </Col>
              {/* Resources */}
              {formData.rag && (
                <>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <ResourceComponent
                        selectedResources={formData.resource_ids_to_rag || []}
                        onSelectResources={(resources) => {
                          onChange("resource_ids_to_rag", resources);
                        }}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              {/* Viz Props */}
              <Col md={12} className="mb-3">
                <VizPropsBuilder
                  vizProps={formData.system_props.viz_props}
                  onChange={(newProps) =>
                    onChangeNested("system_props.viz_props", newProps)
                  }
                />
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button className="action-btn secondary" onClick={onClose}>
              {localization.cancel || "Cancel"}
            </Button>
            <Button
              className="action-btn primary no-border"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <ClipLoader color="var(--primary-icon)" size={20} />
              ) : (
                localization.save || "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
